'use no memo';

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { listAiChatModels } from '@/features/global-ai-chat/api/models';
import type { ChatAgent } from '@/features/global-ai-chat/api/types';
import { orpc } from '@/lib/api/orpc';
import { orpcClient } from '@/lib/api/orpc/client';
import { QUERY_PRESETS } from '@/lib/query';

import { globalAiChatQueryKey } from './key';

/**
 * AI チャットはローカル dev / 一部環境で常時マウントされるグローバルウィジェット。
 * バックエンド（ai-operator）の API キー未設定（503: Chat API key not found）などで
 * 全エンドポイントが失敗しても、
 *  - ブロッキングなグローバルエラーダイアログを出さない（ウィジェット内の toast /
 *    フォールバック表示に委ねる）
 *  - 設定不備は再試行しても回復しないため retry しない（リクエストの乱発を防ぐ）
 *  - エラー状態のクエリは staleTime に関係なく常に stale 扱いとなり、focus / 再接続の
 *    たびに refetch されるため、それらも無効化する（503 固定環境での乱発を断つ）
 */

/** Query / Mutation 双方に共通するリクエスト設定。 */
const AI_CHAT_REQUEST_OPTIONS = {
  meta: { disableGlobalErrorHandling: true },
  retry: false,
} as const;

/** 上記に加え、Query のみエラー状態での focus / 再接続 refetch も止める。 */
const AI_CHAT_QUERY_OPTIONS = {
  ...AI_CHAT_REQUEST_OPTIONS,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

/**
 * 履歴一覧。BE の `listSessions`（oRPC）をカーソルページングで取得し、
 * `data` はフラットな `SessionSummary[]` に整形する。
 * （`fetchNextPage` / `hasNextPage` は query 自体から取得する）
 */
export function useAiChatSessionsQuery() {
  // orpc の infiniteOptions は select 併用時に型推論が壊れるため、
  // queryFn を orpcClient 直呼びにして戻り型から型推論させる（key は orpc 管理を流用）。
  return useInfiniteQuery({
    queryKey: orpc.aiOperatorChat.listSessions.key(),
    queryFn: ({ pageParam }) =>
      orpcClient.aiOperatorChat.listSessions(
        pageParam ? { cursor: pageParam } : {},
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    select: (data) => data.pages.flatMap((page) => page.sessions),
    ...QUERY_PRESETS.list(),
    ...AI_CHAT_QUERY_OPTIONS,
  });
}

/**
 * セッション詳細から messages を取り出す。
 * BE に独立した「メッセージ一覧」エンドポイントはなく `getSession` がメッセージ込みで返すため、
 * `select` で `.messages` のみ抽出する。chatSessionId が null の時は `enabled: false` で停止。
 */
export function useAiChatMessagesQuery(chatSessionId: string | null) {
  return useQuery({
    ...orpc.aiOperatorChat.getSession.queryOptions({
      input: { chatSessionId: chatSessionId ?? '' },
    }),
    enabled: Boolean(chatSessionId),
    select: (data) => data.messages,
    ...QUERY_PRESETS.detail(),
    ...AI_CHAT_QUERY_OPTIONS,
  });
}

/**
 * モデル（回答エージェント）一覧。
 * BE の `GET /v2/ai-operator/chat/agents`（ai-operator の `GET /chat/agents` を中継）を
 * 型付き orpc（`api/models.ts` の listAiChatModels）で取得する。マスタは ai-operator が SSoT。
 */
export function useAiChatModelsQuery() {
  return useQuery({
    queryKey: globalAiChatQueryKey.models(),
    queryFn: listAiChatModels,
    ...QUERY_PRESETS.daily(),
    ...AI_CHAT_QUERY_OPTIONS,
  });
}

export function useCreateAiChatSessionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    ...orpc.aiOperatorChat.createSession.mutationOptions(),
    ...AI_CHAT_REQUEST_OPTIONS,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.aiOperatorChat.listSessions.key(),
      });
    },
  });
}

interface SendAiChatMessageVariables {
  chatSessionId: string;
  input: {
    content: string;
    context?: {
      session?: Record<string, unknown>;
      currentPagePath?: string;
    };
    maxResults?: number;
    /** 参照ナレッジを選ぶ回答エージェント（モデル選択）。未指定時は BE 既定 = 'help'。 */
    agent?: ChatAgent;
  };
}

export function useSendAiChatMessageMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ chatSessionId, input }: SendAiChatMessageVariables) =>
      orpcClient.aiOperatorChat.sendMessage({ chatSessionId, ...input }),
    ...AI_CHAT_REQUEST_OPTIONS,
    onSuccess: () => {
      // メッセージ（getSession）の再取得は呼び出し側 handleSend が
      // 楽観表示の解除タイミング制御のため明示的に refetch するのでここでは行わない。
      // 履歴の並び順・プレビュー更新のため listSessions のみ invalidate する。
      void queryClient.invalidateQueries({
        queryKey: orpc.aiOperatorChat.listSessions.key(),
      });
    },
  });
}
